// tabManager.js - Shared tab management utility

/**
 * Apply consistent highlighting to an element
 */
export const applyHighlighting = (element, duration = 3000) => {
  try {
    const originalBackground = element.style.backgroundColor
    const originalTransition = element.style.transition
    const originalBorder = element.style.border

    element.style.backgroundColor = '#E6EDDD'
    element.style.transition = 'all 0.3s ease'
    element.style.border = '2px solid #C8D4B8'

    setTimeout(() => {
      element.style.backgroundColor = originalBackground
      element.style.border = originalBorder
      setTimeout(() => {
        element.style.transition = originalTransition
      }, 300)
    }, duration)

    // console.log(`TabManager: ✨ Consistent highlighting applied`)
    return true
  } catch (e) {
    // console.warn(`TabManager: ❌ Highlighting failed:`, e)
    return false
  }
}
/**
 * Check if a window's document is ready for DOM manipulation
 */
const isDocumentReady = (windowRef) => {
  try {
    return (
      windowRef &&
      !windowRef.closed &&
      windowRef.document &&
      (windowRef.document.readyState === 'complete' ||
        windowRef.document.readyState === 'interactive')
    )
  } catch (e) {
    return false
  }
}
// Track opened document tabs to prevent duplicates and manage focus
const openedDocumentTabs = new Map() // Store window references by base URL

/**
 * Enhanced element finding with multiple strategies
 */
const findElementInWindow = (windowRef, targetId) => {
  const doc = windowRef.document
  let targetElement = null

  // console.log(`TabManager: Looking for element with ID: ${targetId}`)

  // Strategy 1: Direct getElementById
  targetElement = doc.getElementById(targetId)
  // console.log(
  //   `TabManager: Strategy 1 (getElementById): ${
  //     targetElement ? 'Found' : 'Not found'
  //   }`
  // )

  // Strategy 2: Escaped CSS selector
  if (!targetElement) {
    try {
      const escapedId = targetId.replace(
        /[!"#$%&'()*+,.\/:;<=>?@\[\\\]^`{|}~]/g,
        '\\$&'
      )
      targetElement = doc.querySelector(`#${escapedId}`)
      // console.log(
      //   `TabManager: Strategy 2 (escaped selector): ${
      //     targetElement ? 'Found' : 'Not found'
      //   }`
      // )
    } catch (e) {
      // console.log(`TabManager: Strategy 2 failed:`, e)
    }
  }

  // Strategy 3: Attribute selector
  if (!targetElement) {
    targetElement = doc.querySelector(`[id="${targetId}"]`)
    // console.log(
    //   `TabManager: Strategy 3 (attribute selector): ${
    //     targetElement ? 'Found' : 'Not found'
    //   }`
    // )
  }

  // Strategy 4: Name attribute (for older HTML)
  if (!targetElement) {
    targetElement = doc.querySelector(`[name="${targetId}"]`)
    // console.log(
    //   `TabManager: Strategy 4 (name attribute): ${
    //     targetElement ? 'Found' : 'Not found'
    //   }`
    // )
  }

  // Strategy 5: Anchor with matching name
  if (!targetElement) {
    targetElement = doc.querySelector(`a[name="${targetId}"]`)
    // console.log(
    //   `TabManager: Strategy 5 (anchor name): ${
    //     targetElement ? 'Found' : 'Not found'
    //   }`
    // )
  }

  // Debug: Log available IDs if element not found
  if (!targetElement) {
    const allElementsWithId = doc.querySelectorAll('[id]')
    // console.log(
    //   `TabManager: Found ${allElementsWithId.length} elements with IDs`
    // )

    const availableIds = Array.from(allElementsWithId)
      .map((el) => el.id)
      .filter((id) => id && id.includes('bookmark')) // Focus on bookmark IDs
      .slice(0, 20)
    // console.log(`TabManager: Available bookmark IDs (first 20):`, availableIds)

    // Try exact match with manual search
    for (const element of allElementsWithId) {
      if (element.id === targetId) {
        targetElement = element
        // console.log(`TabManager: Strategy 6 (manual search): Found`)
        break
      }
    }
  }

  return targetElement
}

/**
 * Robust scrolling with multiple methods and attempts
 */
const scrollToElementInWindow = (windowRef, targetElement, targetId) => {
  // console.log(
  //   `TabManager: Attempting to scroll to element:`,
  //   targetElement.tagName,
  //   targetElement.id
  // )
  // Apply highlighting using the helper function

  const highlightSuccess = applyHighlighting(targetElement, 3000)

  if (highlightSuccess) {
    // console.log(`TabManager: ✨ Visual highlight applied successfully`)
  }
  // Method 1: Standard scrollIntoView
  try {
    targetElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })
    // console.log(`TabManager: ✅ Method 1 (scrollIntoView) executed`)
  } catch (e) {
    // console.warn(`TabManager: ❌ Method 1 failed:`, e)
  }

  // Method 2: Calculate position and use window.scrollTo
  try {
    const rect = targetElement.getBoundingClientRect()
    const scrollTop =
      windowRef.pageYOffset || windowRef.document.documentElement.scrollTop
    const targetTop = rect.top + scrollTop - 100 // 100px offset from top

    windowRef.scrollTo({
      top: targetTop,
      behavior: 'smooth',
    })
    // console.log(
    //   `TabManager: ✅ Method 2 (window.scrollTo) executed to position: ${targetTop}`
    // )
  } catch (e) {
    // console.warn(`TabManager: ❌ Method 2 failed:`, e)
  }

  // Method 3: Force immediate scroll (fallback)
  try {
    const rect = targetElement.getBoundingClientRect()
    const scrollTop =
      windowRef.pageYOffset || windowRef.document.documentElement.scrollTop
    const targetTop = rect.top + scrollTop - 100

    windowRef.document.documentElement.scrollTop = targetTop
    windowRef.document.body.scrollTop = targetTop // For older browsers
    // console.log(`TabManager: ✅ Method 3 (direct scrollTop) executed`)
  } catch (e) {
    // console.warn(`TabManager: ❌ Method 3 failed:`, e)
  }
}

/**
 * Opens a URL in a new tab or focuses existing tab if same document is already open
 * @param {string} url - Full URL to open
 * @param {string} linkText - Text content of the link (for article mock)
 * @param {function} showSnackbar - Function to show user feedback
 * @returns {void}
 */
export const openDocumentInTab = (url, linkText, showSnackbar) => {
  // Handle special URL schemes that should not be processed by tab manager
  if (
    url.startsWith('mailto:') ||
    url.startsWith('tel:') ||
    url.startsWith('sms:')
  ) {
    // console.log(`TabManager: Handling special URL scheme: ${url}`)
    window.location.href = url
    if (showSnackbar) {
      const scheme = url.split(':')[0]
      showSnackbar(`Opening ${scheme} link in default application`, 'info')
    }
    return
  }

  // Extract base URL without hash fragment for tracking
  const baseUrl = url.split('#')[0]
  const hashFragment = url.includes('#') ? url.split('#')[1] : null

  // Create slug from URL
  let slug = url
  if (url.startsWith(window.location.origin)) {
    slug = url.replace(window.location.origin, '')
  }

  // console.log(`TabManager: Opening URL: ${url}`)
  // console.log(`TabManager: Base URL: ${baseUrl}`)
  // console.log(`TabManager: Hash: ${hashFragment}`)
  // console.log(`TabManager: Slug: ${slug}`)

  // Check if this base URL (without hash) is already open
  if (openedDocumentTabs.has(baseUrl)) {
    // Tab already exists - focus on it and navigate to hash if needed
    const existingWindow = openedDocumentTabs.get(baseUrl)

    if (existingWindow && !existingWindow.closed) {
      try {
        existingWindow.focus()

        // If there's a hash fragment, navigate to it within the existing tab
        if (hashFragment) {
          // console.log(
          //   `TabManager: Focused existing tab and navigating to: #${hashFragment}`
          // )

          // Update URL hash first
          try {
            existingWindow.location.hash = `#${hashFragment}`
            // console.log(`TabManager: Updated URL hash to: #${hashFragment}`)
          } catch (e) {
            // console.warn(`TabManager: Failed to update hash:`, e)
          }

          // Multiple scroll attempts with increasing delays
          const attempts = [100, 300, 600, 1000, 2000] // milliseconds

          attempts.forEach((delay, index) => {
            setTimeout(() => {
              // console.log(
              //   `TabManager: Scroll attempt ${index + 1} (delay: ${delay}ms)`
              // )

              try {
                const targetElement = findElementInWindow(
                  existingWindow,
                  hashFragment
                )

                if (targetElement) {
                  scrollToElementInWindow(
                    existingWindow,
                    targetElement,
                    hashFragment
                  )
                  // console.log(
                  //   `TabManager: ✅ Scroll attempt ${
                  //     index + 1
                  //   } completed successfully`
                  // )
                  return
                } else {
                  // console.log(
                  //   `TabManager: ❌ Scroll attempt ${
                  //     index + 1
                  //   }: Element not found yet`
                  // )

                  // On the last attempt, try native hash navigation WITH highlighting
                  if (index === attempts.length - 1) {
                    // console.log(
                    //   `TabManager: 🔄 Final attempt: Using native hash navigation`
                    // )
                    try {
                      const currentUrl =
                        existingWindow.location.href.split('#')[0]
                      existingWindow.location.href = `${currentUrl}#${hashFragment}`
                      // Try to highlight after native navigation
                      setTimeout(() => {
                        const targetElement = findElementInWindow(
                          existingWindow,
                          hashFragment
                        )
                        if (targetElement) {
                          const success = applyHighlighting(targetElement, 3000)
                          if (success) {
                            // console.log(
                            //   `TabManager: ✅ Native navigation highlighting successful`
                            // )
                          }
                        }
                      }, 500)
                    } catch (e) {
                      // console.warn(
                      //   `TabManager: ❌ Native navigation highlight failed:`,
                      //   e
                      // )
                    }
                  }
                }
              } catch (e) {
                // console.warn(
                //   `TabManager: ❌ Scroll attempt ${index + 1} failed:`,
                //   e
                // )
              }
            }, delay)
          })

          if (showSnackbar) {
            showSnackbar(
              `Navigated to section in existing tab: ${hashFragment}`,
              'info'
            )
          }
        } else {
          // console.log(`TabManager: Focused existing tab: ${baseUrl}`)

          if (showSnackbar) {
            showSnackbar('Switched to existing document tab', 'info')
          }
        }
        return // Exit early - no need to open new tab
      } catch (error) {
        // console.log('TabManager: Could not focus existing tab, opening new one')
        // If focus fails, remove from tracking and open new tab
        openedDocumentTabs.delete(baseUrl)
      }
    } else {
      // Window was closed, remove from tracking and open new tab
      openedDocumentTabs.delete(baseUrl)
    }
  }

  // No existing tab or focus failed - open new one
  openNewDocumentTab(url, slug, linkText, showSnackbar, baseUrl)
}

/**
 * Opens a new tab for a document
 * @param {string} fullUrl - Full URL to open
 * @param {string} slug - URL slug/path
 * @param {string} linkText - Text content for feedback
 * @param {function} showSnackbar - Function to show user feedback
 * @param {string} baseUrl - Base URL without hash for tracking
 * @returns {void}
 */
function openNewDocumentTab(fullUrl, slug, linkText, showSnackbar, baseUrl) {
  const newWindow = window.open(slug, '_blank')

  if (newWindow) {
    newWindow.focus()

    // Store window reference using base URL (without hash) for future focus operations
    openedDocumentTabs.set(baseUrl, newWindow)
    // Handle highlighting for new tabs with hash fragments
    // reason to handle it separately: The first link opens a new tab but doesn't trigger the scroll attempts because there's no existing tab to focus on. The highlighting only happens during the scroll attempts, which only occur when focusing an existing tab
    const hashFragment = fullUrl.includes('#') ? fullUrl.split('#')[1] : null

    if (hashFragment) {
      // console.log(
      //   `TabManager: New tab opened with hash fragment: #${hashFragment}`
      // )

      // More robust highlighting for new tabs
      const highlightInNewTab = () => {
        const maxAttempts = 10
        let attempt = 0

        const tryHighlight = () => {
          attempt++
          // console.log(
          //   `TabManager: New tab highlight attempt ${attempt}/${maxAttempts}`
          // )

          if (attempt > maxAttempts) {
            // console.log(
            //   `TabManager: ❌ Gave up highlighting after ${maxAttempts} attempts`
            // )
            return
          }

          if (!isDocumentReady(newWindow)) {
            // console.log(`TabManager: ⏳ Document not ready, retrying in 500ms`)
            setTimeout(tryHighlight, 500)
            return
          }

          const targetElement = findElementInWindow(newWindow, hashFragment)
          if (targetElement) {
            const success = applyHighlighting(targetElement, 3000)
            if (success) {
              // console.log(
              //   `TabManager: ✅ New tab highlighting successful on attempt ${attempt}`
              // )
              return
            }
          }

          // Retry after a short delay
          setTimeout(tryHighlight, 300)
        }

        // Start trying after initial delay
        setTimeout(tryHighlight, 500)
      }

      highlightInNewTab()
    }
    // Monitor if the tab gets closed so we can clean up tracking
    const checkClosed = setInterval(() => {
      if (newWindow.closed) {
        openedDocumentTabs.delete(baseUrl)
        clearInterval(checkClosed)
        // console.log(`TabManager: Cleaned up closed tab: ${baseUrl}`)
      }
    }, 1000)

    // console.log(`TabManager: Opened new tab: ${fullUrl}`)

    if (showSnackbar) {
      const documentTitle = linkText || 'Document'
      showSnackbar(`${documentTitle} opened in new tab`, 'success')
    }
  } else {
    // console.log('TabManager: Failed to open new tab - popup might be blocked')
    if (showSnackbar) {
      showSnackbar('Could not open new tab - check popup blocker', 'error')
    }
  }
}

/**
 * Legacy function for compatibility with existing articleCardHandlers
 * @param {object} article - Article object
 * @param {string} slug - URL slug
 * @param {function} showSnackbar - Snackbar function
 * @returns {void}
 */
export const handleOpenInNewTab = (article, slug, showSnackbar) => {
  // Create the full URL
  const fullUrl = `${window.location.origin}${slug}`
  const linkText = article?.title || article?.name || 'Document'

  // Use the new shared tab manager
  openDocumentInTab(fullUrl, linkText, showSnackbar)
}

/**
 * Get current tracking status for debugging
 * @returns {object} Current tracking information
 */
export const getTrackingInfo = () => {
  const trackedUrls = Array.from(openedDocumentTabs.keys())
  const openTabs = trackedUrls.filter((url) => {
    const window = openedDocumentTabs.get(url)
    return window && !window.closed
  })

  return {
    totalTracked: trackedUrls.length,
    openTabs: openTabs.length,
    trackedUrls,
    openTabUrls: openTabs,
  }
}

/**
 * Clean up function to clear all tracking
 * @returns {void}
 */
export const clearDocumentTabsTracking = () => {
  openedDocumentTabs.clear()
  // console.log('TabManager: Cleared all tab tracking')
}
